import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, ChevronUp, ChevronDown, Save, RotateCcw, AlertTriangle } from 'lucide-react';
// Using the same types as RiskMatrix
interface ParamDef {
  id: string;
  label: string;
  weight: number;
  dataAvailable: boolean;
  mapFromFactor?: string;
  pendingReason?: string;
  aiEvaluated?: boolean;
  tooltip?: string;
}
interface CategoryDef {
  id: string;
  label: string;
  icon: string;
  params: ParamDef[];
  tooltip?: string;
}
interface ModelCustomizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCategories: CategoryDef[];
  defaultCategories: CategoryDef[];
  onSave: (categories: CategoryDef[]) => void;
}
const EMOJI_LIST = ['📝', '👁️', '👷', '⚠️', '🌤', '🤝', '🏗️', '🔥', '⚡', '🛡️', '📊', '📋'];
export function ModelCustomizationModal({
  isOpen,
  onClose,
  initialCategories,
  defaultCategories,
  onSave
}: ModelCustomizationModalProps) {
  const [categories, setCategories] = useState<CategoryDef[]>(JSON.parse(JSON.stringify(initialCategories)));
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(categories[0]?.id || null);
  const [showEmojiPickerFor, setShowEmojiPickerFor] = useState<string | null>(null);
  // Reset to initial state when opened
  useEffect(() => {
    if (isOpen) {
      setCategories(JSON.parse(JSON.stringify(initialCategories)));
      setSelectedCategoryId(initialCategories[0]?.id || null);
    }
  }, [isOpen, initialCategories]);
  if (!isOpen) return null;
  const selectedCategoryIndex = categories.findIndex(c => c.id === selectedCategoryId);
  const selectedCategory = categories[selectedCategoryIndex];
  // Category Actions
  const handleAddCategory = () => {
    const newId = `cat_${Date.now()}`;
    const newCat: CategoryDef = {
      id: newId,
      label: 'New Category',
      icon: '📋',
      params: []
    };
    setCategories([...categories, newCat]);
    setSelectedCategoryId(newId);
  };
  const handleDeleteCategory = (id: string) => {
    if (confirm('Are you sure? This will remove this category and all its parameters from the model.')) {
      const newCategories = categories.filter(c => c.id !== id);
      setCategories(newCategories);
      if (selectedCategoryId === id) {
        setSelectedCategoryId(newCategories[0]?.id || null);
      }
    }
  };
  const handleMoveCategory = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === categories.length - 1) return;
    const newCategories = [...categories];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newCategories[index];
    newCategories[index] = newCategories[swapIndex];
    newCategories[swapIndex] = temp;
    setCategories(newCategories);
  };
  const handleUpdateCategory = (id: string, updates: Partial<CategoryDef>) => {
    setCategories(categories.map(c => c.id === id ? {
      ...c,
      ...updates
    } : c));
  };
  // Parameter Actions
  const handleAddParameter = () => {
    if (!selectedCategory) return;
    const newParam: ParamDef = {
      id: `param_${Date.now()}`,
      label: 'New Parameter',
      weight: 5,
      dataAvailable: false,
      tooltip: ''
    };
    const updatedCat = {
      ...selectedCategory,
      params: [...selectedCategory.params, newParam]
    };
    handleUpdateCategory(selectedCategory.id, updatedCat);
  };
  const handleDeleteParameter = (paramId: string) => {
    if (!selectedCategory) return;
    if (confirm('Are you sure? This will remove this parameter from the model.')) {
      const updatedCat = {
        ...selectedCategory,
        params: selectedCategory.params.filter(p => p.id !== paramId)
      };
      handleUpdateCategory(selectedCategory.id, updatedCat);
    }
  };
  const handleMoveParameter = (index: number, direction: 'up' | 'down') => {
    if (!selectedCategory) return;
    if (direction === 'up' && index === 0 || direction === 'down' && index === selectedCategory.params.length - 1) return;
    const newParams = [...selectedCategory.params];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newParams[index];
    newParams[index] = newParams[swapIndex];
    newParams[swapIndex] = temp;
    handleUpdateCategory(selectedCategory.id, {
      params: newParams
    });
  };
  const handleUpdateParameter = (paramId: string, updates: Partial<ParamDef>) => {
    if (!selectedCategory) return;
    const updatedCat = {
      ...selectedCategory,
      params: selectedCategory.params.map(p => p.id === paramId ? {
        ...p,
        ...updates
      } : p)
    };
    handleUpdateCategory(selectedCategory.id, updatedCat);
  };
  const handleReset = () => {
    if (confirm('Are you sure you want to reset to the default model? Any custom categories or parameters will be lost.')) {
      setCategories(JSON.parse(JSON.stringify(defaultCategories)));
      setSelectedCategoryId(defaultCategories[0]?.id || null);
    }
  };
  const handleSave = () => {
    onSave(categories);
    onClose();
  };
  return <AnimatePresence data-id="element-1285" data-mp="e7aoyb44pyna">
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-1286" data-mp="e72c2j80atc3">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose} data-id="element-1287" data-mp="izm0ycc59aw7" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-6xl h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-1288" data-mp="3rff5rtx44xxf">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0" data-id="element-1289" data-mp="1n8m23x1k4ind">
            <div data-id="element-1290" data-mp="2kv1mp7pa5wf5">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1291" data-mp="2kvktbs9hcydv">
                Customize Risk Model
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1292" data-mp="2fux49c4tarma">
                Create, edit, and reorder the categories and parameters used in
                risk scoring.
              </p>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1293" data-mp="26yeeg8rvnjtx">
              <X size={20} data-id="element-1294" data-mp="vflepvn3egqu" />
            </button>
          </div>

          {/* Body: Two Panels */}
          <div className="flex flex-1 min-h-0 overflow-hidden bg-gray-50" data-id="element-1295" data-mp="3762zqfo7xkq3">
            {/* Left Panel: Categories */}
            <div className="w-1/3 border-r border-gray-200 bg-white flex flex-col" data-id="element-1296" data-mp="3b40q3ltq6cv2">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center" data-id="element-1297" data-mp="3g3n07me9bczn">
                <h3 className="text-sm font-semibold text-gray-700" data-id="element-1298" data-mp="3kxzpid2ezijb">
                  Categories
                </h3>
                <span className="text-xs text-gray-500" data-id="element-1299" data-mp="3uu2oja1yz121">
                  {categories.length} total
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2" data-id="element-1300" data-mp="2rlccgbyzclz">
                {categories.map((cat, idx) => <div key={cat.id} className={`group relative flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${selectedCategoryId === cat.id ? 'bg-indigo-50 border-indigo-200 shadow-sm' : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-sm'}`} onClick={() => setSelectedCategoryId(cat.id)} data-id="element-1301" data-mp="7olj6tgn8je5" data-mpi={idx}>
                    {/* Reorder Handles */}
                    <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1302" data-mp="oyyv2mxn2ah5">
                      <button onClick={e => {
                    e.stopPropagation();
                    handleMoveCategory(idx, 'up');
                  }} disabled={idx === 0} className="p-0.5 text-gray-400 hover:text-indigo-600 disabled:opacity-30" data-id="element-1303" data-mp="k0nlmdb9a1zg">
                        <ChevronUp size={14} data-id="element-1304" data-mp="19e9rxe0j14p" />
                      </button>
                      <button onClick={e => {
                    e.stopPropagation();
                    handleMoveCategory(idx, 'down');
                  }} disabled={idx === categories.length - 1} className="p-0.5 text-gray-400 hover:text-indigo-600 disabled:opacity-30" data-id="element-1305" data-mp="1bh9581226m3">
                        <ChevronDown size={14} data-id="element-1306" data-mp="2ziwr28z8rbfz" />
                      </button>
                    </div>

                    {/* Icon Picker */}
                    <div className="relative" data-id="element-1307" data-mp="2qjjfnark4csm">
                      <button onClick={e => {
                    e.stopPropagation();
                    setShowEmojiPickerFor(showEmojiPickerFor === cat.id ? null : cat.id);
                  }} className="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-lg hover:bg-gray-50 transition-colors" data-id="element-1308" data-mp="2qmfcswvkyxg0">
                        {cat.icon}
                      </button>
                      {showEmojiPickerFor === cat.id && <div className="absolute top-full left-0 mt-1 p-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 grid grid-cols-4 gap-1 w-48" data-id="element-1309" data-mp="5vx2wg5a6al0">
                          {EMOJI_LIST.map(emoji => <button key={emoji} onClick={e => {
                      e.stopPropagation();
                      handleUpdateCategory(cat.id, {
                        icon: emoji
                      });
                      setShowEmojiPickerFor(null);
                    }} className="p-2 hover:bg-gray-100 rounded text-lg" data-id="element-1310" data-mp="2yegng0ig4rpq">
                              {emoji}
                            </button>)}
                        </div>}
                    </div>

                    {/* Name Input */}
                    <div className="flex-1 min-w-0" data-id="element-1311" data-mp="2ao2ve9zv2sv5">
                      <input type="text" value={cat.label} onChange={e => handleUpdateCategory(cat.id, {
                    label: e.target.value
                  })} onClick={e => e.stopPropagation()} className={`w-full text-sm font-semibold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-indigo-500 focus:outline-none transition-colors px-1 py-0.5 ${selectedCategoryId === cat.id ? 'text-indigo-900' : 'text-gray-900'}`} data-id="element-1312" data-mp="1r2kg292uoclf" />
                      <div className="text-xs text-gray-500 px-1 mt-0.5" data-id="element-1313" data-mp="2jtawf33lzlak">
                        {cat.params.length} parameters
                      </div>
                    </div>

                    {/* Delete */}
                    <button onClick={e => {
                  e.stopPropagation();
                  handleDeleteCategory(cat.id);
                }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-all" title="Delete Category" data-id="element-1314" data-mp="2nt35ri0ttvgz">
                      <Trash2 size={16} data-id="element-1315" data-mp="1z6tqtrcoo7c8" />
                    </button>
                  </div>)}
              </div>
              <div className="p-4 border-t border-gray-200 bg-white" data-id="element-1316" data-mp="2453n65a59dzf">
                <button onClick={handleAddCategory} className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all" data-id="element-1317" data-mp="294q8z341g2ju">
                  <Plus size={16} data-id="element-1318" data-mp="jrw7dnd08wc6" />
                  Add Category
                </button>
              </div>
            </div>

            {/* Right Panel: Parameters */}
            <div className="flex-1 flex flex-col bg-gray-50" data-id="element-1319" data-mp="g8nmy8p84r8f">
              {selectedCategory ? <>
                  <div className="px-6 py-4 border-b border-gray-200 bg-white flex justify-between items-center shadow-sm z-10" data-id="element-1320" data-mp="bgvwrz9zcnit">
                    <div data-id="element-1321" data-mp="6ghyva23etyi">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-1322" data-mp="2h954571yw2y">
                        <span data-id="element-1323" data-mp="z6zyg13lyd76">{selectedCategory.icon}</span>
                        {selectedCategory.label}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5" data-id="element-1324" data-mp="u983szbkwcza">
                        Manage parameters for this category
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 space-y-3" data-id="element-1325" data-mp="l6p5msiqmojd">
                    {selectedCategory.params.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200 border-dashed" data-id="element-1326" data-mp="3aupvvkpdwswe">
                        <p className="text-gray-500 text-sm" data-id="element-1327" data-mp="xozz8gfcj750">
                          No parameters in this category.
                        </p>
                      </div> : selectedCategory.params.map((param, idx) => <div key={param.id} className="group bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all flex gap-4" data-id="element-1328" data-mp="oq630r5xgirz" data-mpi={idx}>
                          {/* Reorder Handles */}
                          <div className="flex flex-col gap-1 pt-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1329" data-mp="1lc84iycryg71">
                            <button onClick={() => handleMoveParameter(idx, 'up')} disabled={idx === 0} className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded disabled:opacity-30 disabled:hover:bg-transparent" data-id="element-1330" data-mp="1cdnracpzgyq7">
                              <ChevronUp size={16} data-id="element-1331" data-mp="3olb5haw8iaba" />
                            </button>
                            <button onClick={() => handleMoveParameter(idx, 'down')} disabled={idx === selectedCategory.params.length - 1} className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded disabled:opacity-30 disabled:hover:bg-transparent" data-id="element-1332" data-mp="3kkzl7z7sac0c">
                              <ChevronDown size={16} data-id="element-1333" data-mp="3fm69ab85at2a" />
                            </button>
                          </div>

                          <div className="flex-1 space-y-3" data-id="element-1334" data-mp="3auoi2evxc6ui">
                            <div className="flex items-start justify-between gap-4" data-id="element-1335" data-mp="2n65a60gsdw2y">
                              <div className="flex-1" data-id="element-1336" data-mp="2n3si2uhbe5c6">
                                <input type="text" value={param.label} onChange={e => handleUpdateParameter(param.id, {
                          label: e.target.value
                        })} placeholder="Parameter Name" className="w-full text-base font-bold text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-indigo-500 focus:outline-none transition-colors px-1 py-0.5" data-id="element-1337" data-mp="2i60u2bjsj3d9" />
                              </div>
                              <div className="flex items-center gap-3" data-id="element-1338" data-mp="h4t4i9qrvbum">
                                {!param.dataAvailable && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200" data-id="element-1339" data-mp="1nlocf493cbt5">
                                    <AlertTriangle size={12} data-id="element-1340" data-mp="22nn2rsg8kg72" />
                                    Pending Data
                                  </span>}
                                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1341" data-mp="1sy3kop0ckqd9">
                                  <input type="checkbox" checked={param.aiEvaluated || false} onChange={e => handleUpdateParameter(param.id, {
                            aiEvaluated: e.target.checked
                          })} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" data-id="element-1342" data-mp="1svqu5ngix7ol" />
                                  <span className="text-xs font-medium text-gray-600 flex items-center gap-1" data-id="element-1343" data-mp="34b5zbeivz5us">
                                    🤖 AI Evaluated
                                  </span>
                                </label>
                                <button onClick={() => handleDeleteParameter(param.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Delete Parameter" data-id="element-1344" data-mp="3nmjmvc7d9w9i">
                                  <Trash2 size={16} data-id="element-1345" data-mp="34wlf6x7he56e" />
                                </button>
                              </div>
                            </div>
                            <div data-id="element-1346" data-mp="2vuu9hscxxycj">
                              <textarea value={param.tooltip || ''} onChange={e => handleUpdateParameter(param.id, {
                        tooltip: e.target.value
                      })} placeholder="Description / Tooltip (explains what this parameter measures)" rows={2} className="w-full text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-2.5 focus:bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none transition-all" data-id="element-1347" data-mp="2vvvmw3hfiwxf" />
                            </div>
                          </div>
                        </div>)}
                    <button onClick={handleAddParameter} className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all mt-4" data-id="element-1348" data-mp="1vxak5fk5lha0">
                      <Plus size={16} data-id="element-1349" data-mp="312eqim0mm4z0" />
                      Add Parameter
                    </button>
                  </div>
                </> : <div className="flex-1 flex items-center justify-center text-gray-400" data-id="element-1350" data-mp="2w31x1if91zng">
                  Select a category to view parameters
                </div>}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white flex-shrink-0" data-id="element-1351" data-mp="2i3aldoc7gvcm">
            <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1352" data-mp="2d596zgpq5k7h">
              <RotateCcw size={16} data-id="element-1353" data-mp="2n1c4uadjmwom" />
              Reset to Default
            </button>
            <div className="flex items-center gap-3" data-id="element-1354" data-mp="6cf1kx96prvd">
              <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1355" data-mp="2codj1b1lpuj">
                Cancel
              </button>
              <button onClick={handleSave} className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors" data-id="element-1356" data-mp="evs3a45p6vqe">
                <Save size={16} data-id="element-1357" data-mp="smf3k0r3wtol" />
                Save & Apply
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}